// FinKloz — Dynamic Cap Table
// Ownership chart (clickable filter + navigation), side-by-side diluted/undiluted,
// inline editing with audit trail, CSV export, scenario modeling, Raise a Round wizard,
// Canadian ownership tracking with SPCC warning.

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { sans, mono, green, greenLight, greenDark, coral, coralSoft, red, redLight, amber, amberLight, useTheme } from '../../tokens';
import {
  CAPTABLE_ROUNDS as SHARED_ROUNDS,
  CAPTABLE_SHAREHOLDERS as SHARED_SHAREHOLDERS,
  CAPTABLE_OPTION_POOL as SHARED_OPTION_POOL,
  loadCapTableEdits,
  saveCapTableEdits,
} from '../../data/capTableSource';

const teal = '#0D9488';

// ─── Data ────────────────────────────────────────────────────────────────────────
// Cap table data lives in src/data/capTableSource.js so that both this
// full table AND the investor OwnershipCard read from a single source.
const INITIAL_ROUNDS = SHARED_ROUNDS;
const INITIAL_SHAREHOLDERS = SHARED_SHAREHOLDERS;
const OPTION_POOL = SHARED_OPTION_POOL;

function fmt(n) { if (!n || n === 0) return ''; return Math.round(n).toLocaleString('en-CA'); }
function pctFmt(n) { if (!n) return ''; return `${(n * 100).toFixed(2)}%`; }
function fmtMoney(n) { if (!n) return '$0'; const abs = Math.abs(n); if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`; if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`; return `$${n}`; }

// Soft pastel category colors — simplified: no Investor vs Angel distinction
const CAT_COLORS = {
  Founders: '#93C5FD',
  Shareholder: '#86EFAC',
  'Purchased options': '#C4B5FD',
  'Option Pool': '#FDE68A',
};

function getCat(sh) {
  if (sh.type === 'Founders') return 'Founders';
  if (sh.type === 'Purchased options') return 'Purchased options';
  return 'Shareholder';
}

// ─── Component ───────────────────────────────────────────────────────────────────
export default function CapTable({ companyId, onNavigateTab }) {
  const t = useTheme();
  // Hydrate from localStorage on mount so Cap Table edits persist across reloads.
  const _edits = loadCapTableEdits();
  const [shareholders, setShareholders] = useState(_edits?.shareholders || INITIAL_SHAREHOLDERS);
  const [rounds, setRounds] = useState(_edits?.rounds || INITIAL_ROUNDS);
  const [optionPoolState, setOptionPoolState] = useState(_edits?.optionPool || OPTION_POOL);
  // Persist any edit back to the shared source so OwnershipCard stays in sync.
  useEffect(() => {
    saveCapTableEdits({ rounds, shareholders, optionPool: optionPoolState });
  }, [rounds, shareholders, optionPoolState]);
  const [search, setSearch] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editingPrice, setEditingPrice] = useState(null); // roundId being edited
  const [priceValue, setPriceValue] = useState('');
  const [showChart, setShowChart] = useState(true);
  const [capSortBy, setCapSortBy] = useState(null);
  const [capSortDir, setCapSortDir] = useState('asc');

  const handleCapSort = (col) => {
    if (capSortBy === col) {
      if (capSortDir === 'asc') setCapSortDir('desc');
      else { setCapSortBy(null); setCapSortDir('asc'); } // reset on third click
    } else { setCapSortBy(col); setCapSortDir('asc'); }
  };
  const [showScenario, setShowScenario] = useState(false);
  const [scenario, setScenario] = useState({ roundSize: 30000000, preMoney: 120000000, newPoolPct: 15, currency: 'CAD', fxRate: 1.3870, canPct: 50 });
  const [filterCategory, setFilterCategory] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showRaiseWizard, setShowRaiseWizard] = useState(false);
  const [raiseStep, setRaiseStep] = useState(1);
  const [raiseData, setRaiseData] = useState({
    name: '', roundType: 'Equity', roundSize: 0, currency: 'CAD', fxRate: 1.3870, closeDate: '',
    preMoney: 0, esopShuffle: false,
    targetPoolPct: 15,
    investors: [{ name: '', amount: 0, canadian: true }],
    liquidationPref: '1x', antiDilution: 'broad-based', boardSeats: 0, boardSeatHolder: '', proRata: true, sideLetters: '',
    status: 'draft',
  });
  const [selectedShareholder, setSelectedShareholder] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [editingPanelRound, setEditingPanelRound] = useState(null);
  const [panelEditValue, setPanelEditValue] = useState('');

  // Audit log helper
  const logChange = useCallback((action, field, oldVal, newVal, shareholderName) => {
    setAuditLog(prev => [...prev, {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      action,
      field,
      oldValue: oldVal != null ? String(oldVal) : null,
      newValue: newVal != null ? String(newVal) : null,
      shareholder: shareholderName || null,
      user: 'KAS',
    }]);
  }, []);

  // Computed data
  const data = useMemo(() => {
    let totalShares = 0;
    const rows = shareholders.map(sh => {
      const total = Object.values(sh.h).reduce((s, v) => s + v, 0);
      totalShares += total;
      return { ...sh, totalShares: total };
    });
    const fdTotal = totalShares + OPTION_POOL.outstanding;
    return {
      rows: rows.map(r => ({
        ...r,
        undilutedPct: r.totalShares / totalShares,
        fullyDilutedPct: r.totalShares / fdTotal,
      })),
      totalShares,
      fdTotal,
    };
  }, [shareholders]);

  const filtered = useMemo(() => {
    const rows = data.rows.filter(r => {
      if (search && !r.name.toLowerCase().includes(search.toLowerCase())) return false;
      if (filterCategory && getCat(r) !== filterCategory) return false;
      return true;
    });
    if (capSortBy) {
      rows.sort((a, b) => {
        let va, vb;
        if (capSortBy === 'totalShares' || capSortBy === 'undilutedPct' || capSortBy === 'fullyDilutedPct') {
          va = a[capSortBy]; vb = b[capSortBy];
        } else if (capSortBy === 'name' || capSortBy === 'country') {
          va = (a[capSortBy] || '').toLowerCase(); vb = (b[capSortBy] || '').toLowerCase();
        } else {
          // Round column
          va = a.h[capSortBy] || 0; vb = b.h[capSortBy] || 0;
        }
        if (va < vb) return capSortDir === 'asc' ? -1 : 1;
        if (va > vb) return capSortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return rows;
  }, [data.rows, search, filterCategory, capSortBy, capSortDir]);

  const canadianUndiluted = data.rows.filter(r => r.country === 'CAN').reduce((s, r) => s + r.undilutedPct, 0);
  const canadianFD = data.rows.filter(r => r.country === 'CAN').reduce((s, r) => s + r.fullyDilutedPct, 0);
  // Ownership by category
  const categories = useMemo(() => {
    const cats = {};
    data.rows.forEach(r => {
      const cat = getCat(r);
      if (!cats[cat]) cats[cat] = 0;
      cats[cat] += r.totalShares;
    });
    cats['Option Pool'] = OPTION_POOL.outstanding;
    const total = Object.values(cats).reduce((s, v) => s + v, 0);
    return Object.entries(cats).map(([name, shares]) => ({
      name, shares, pct: shares / total, color: CAT_COLORS[name] || '#D1D5DB',
    })).sort((a, b) => b.shares - a.shares);
  }, [data.rows]);

  // Edit handlers
  const startEdit = (rowIdx, roundId, currentVal) => {
    setEditingCell({ rowIdx, roundId });
    setEditValue(currentVal ? String(Math.round(currentVal)) : '');
  };
  const commitEdit = () => {
    if (!editingCell) return;
    const { rowIdx, roundId } = editingCell;
    const newVal = parseFloat(editValue) || 0;
    const oldVal = shareholders[rowIdx]?.h?.[roundId] || 0;
    if (newVal !== oldVal) {
      logChange('update', `${roundId} shares`, Math.round(oldVal), Math.round(newVal), shareholders[rowIdx].name);
      setShareholders(prev => {
        const next = [...prev];
        next[rowIdx] = { ...next[rowIdx], h: { ...next[rowIdx].h, [roundId]: newVal || undefined } };
        if (!newVal) delete next[rowIdx].h[roundId];
        return next;
      });
    }
    setEditingCell(null);
  };

  // Price edit
  const startPriceEdit = (roundId, currentPrice) => {
    setEditingPrice(roundId);
    setPriceValue(currentPrice ? String(currentPrice) : '');
  };
  const commitPriceEdit = () => {
    if (!editingPrice) return;
    const newPrice = parseFloat(priceValue) || 0;
    const oldRound = rounds.find(r => r.id === editingPrice);
    if (oldRound && newPrice > 0 && newPrice !== oldRound.price) {
      logChange('update', `${oldRound.name} price`, oldRound.price, newPrice);
      setRounds(prev => prev.map(r => r.id === editingPrice ? { ...r, price: newPrice } : r));
    }
    setEditingPrice(null);
  };

  // CSV export
  const exportCSV = useCallback(() => {
    const headers = ['Shareholder', 'Country', ...rounds.map(r => r.name), 'Total Shares', 'Undiluted %', 'Fully Diluted %'];
    const csvRows = [headers.join(',')];
    data.rows.forEach(r => {
      csvRows.push([`"${r.name}"`, r.country, ...rounds.map(rd => r.h[rd.id] ? Math.round(r.h[rd.id]) : ''), Math.round(r.totalShares), (r.undilutedPct * 100).toFixed(4) + '%', (r.fullyDilutedPct * 100).toFixed(4) + '%'].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'cap-table-export.csv'; a.click();
    URL.revokeObjectURL(url);
  }, [data, rounds]);

  // Export audit log
  const exportAuditCSV = useCallback(() => {
    const headers = ['Timestamp', 'User', 'Action', 'Field', 'Shareholder', 'Old Value', 'New Value'];
    const csvRows = [headers.join(',')];
    auditLog.forEach(e => {
      csvRows.push([e.timestamp, e.user, e.action, e.field, `"${e.shareholder || ''}"`, e.oldValue || '', e.newValue || ''].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'cap-table-audit-log.csv'; a.click();
    URL.revokeObjectURL(url);
  }, [auditLog]);

  // Raise a Round — execute
  // Raise a Round — computed values
  const raiseCalc = useMemo(() => {
    if (!raiseData.preMoney || !data.fdTotal) return {};
    const pps = raiseData.preMoney / data.fdTotal;
    const totalInvested = raiseData.investors.reduce((s, inv) => s + (inv.amount || 0), 0);
    const newShares = pps > 0 ? Math.round(totalInvested / pps) : 0;
    const newPoolTarget = Math.round(data.fdTotal * (raiseData.targetPoolPct / 100));
    const poolExpansion = Math.max(0, newPoolTarget - OPTION_POOL.total);
    const postFD = data.fdTotal + newShares + (raiseData.esopShuffle ? poolExpansion : 0);
    const canShares = data.rows.filter(r => r.country === 'CAN').reduce((s, r) => s + r.totalShares, 0);
    const canInvShares = raiseData.investors.filter(inv => inv.canadian).reduce((s, inv) => s + (pps > 0 ? Math.round(inv.amount / pps) : 0), 0);
    const canPctPost = (canShares + canInvShares) / postFD;
    return { pps, totalInvested, newShares, poolExpansion, postFD, canPctPost, postMoney: raiseData.preMoney + totalInvested };
  }, [raiseData, data]);

  const executeRound = () => {
    const { pps } = raiseCalc;
    const newRoundId = raiseData.name.toLowerCase().replace(/[^a-z0-9]/g, '');

    // Add new round column
    setRounds(prev => [...prev, {
      id: newRoundId, name: raiseData.name, short: raiseData.name,
      price: pps, date: raiseData.closeDate || new Date().toISOString().slice(0, 7),
      currency: raiseData.currency, fxRate: raiseData.fxRate,
    }]);

    // Add shares per investor
    raiseData.investors.forEach(inv => {
      if (!inv.name || !inv.amount) return;
      const shares = pps > 0 ? Math.round(inv.amount / pps) : 0;
      const existingIdx = shareholders.findIndex(s => s.name === inv.name);
      if (existingIdx >= 0) {
        setShareholders(prev => {
          const next = [...prev];
          next[existingIdx] = { ...next[existingIdx], h: { ...next[existingIdx].h, [newRoundId]: shares } };
          return next;
        });
      } else {
        setShareholders(prev => [...prev, { name: inv.name, country: inv.canadian ? 'CAN' : 'INT', type: 'Investor', h: { [newRoundId]: shares } }]);
      }
    });

    const details = [
      `${raiseData.currency} ${fmtMoney(raiseCalc.totalInvested)} at ${fmtMoney(raiseData.preMoney)} pre-money`,
      raiseData.liquidationPref !== '1x' ? `Liq pref: ${raiseData.liquidationPref}` : '',
      raiseData.esopShuffle ? `Pool expanded to ${raiseData.targetPoolPct}%` : '',
    ].filter(Boolean).join(' | ');
    logChange('round', raiseData.name, null, details);
    setShowRaiseWizard(false);
    setRaiseStep(1);
  };

  // Navigation helper for chart clicks
  const handleChartClick = (catName) => {
    if (catName === 'Option Pool' || catName === 'Purchased options') {
      if (onNavigateTab) onNavigateTab('options');
    } else {
      setFilterCategory(filterCategory === catName ? null : catName);
    }
  };

  // Clickable card helper
  const kpiCard = (label, value, color, onClick) => (
    <div onClick={onClick} style={{
      padding: '12px 16px',
      borderLeft: `3px solid ${color || t.border}`,
      cursor: onClick ? 'pointer' : 'default', transition: 'all 0.15s',
    }}
      onMouseEnter={onClick ? e => { e.currentTarget.style.background = t.bgSurface || '#f9fafb'; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.background = 'transparent'; } : undefined}
    >
      <div style={{ fontFamily: mono, fontSize: 22, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginTop: 4 }}>{label}</div>
    </div>
  );

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 16 }}>
        {kpiCard('Fully Diluted', fmt(Math.round(data.fdTotal)), green)}
        {kpiCard('Option Pool', fmt(OPTION_POOL.total), t.textPrimary, onNavigateTab ? () => onNavigateTab('options') : undefined)}
        {kpiCard('Available Options', fmt(Math.round(OPTION_POOL.available)), teal, onNavigateTab ? () => onNavigateTab('options') : undefined)}
        {kpiCard('Outstanding Options', fmt(Math.round(OPTION_POOL.outstanding)), amber, onNavigateTab ? () => onNavigateTab('options') : undefined)}
        <div style={{ padding: '12px 16px', borderLeft: `3px solid ${canadianFD >= 0.5 ? green : red}` }}>
          <div style={{ fontFamily: mono, fontSize: 22, fontWeight: 700, color: canadianFD >= 0.5 ? green : red, lineHeight: 1.1 }}>{pctFmt(canadianFD)}</div>
          <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginTop: 4 }}>Canadian Ownership (FD)</div>
          {canadianFD < 0.5 && <div style={{ fontFamily: sans, fontSize: 8, color: red, fontWeight: 600, marginTop: 2 }}>Below 50% — SPCC/SR&ED risk</div>}
        </div>
      </div>

      {/* Pool health badge — displayed on Stock Options tab only */}

      {/* Ownership Chart */}
      {showChart && (
        <div style={{ marginBottom: 16, padding: '16px 20px', background: t.bgCard, border: `1px solid ${t.border}`, borderLeft: `3px solid ${greenLight}`, borderRadius: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, fontFamily: sans }}>Ownership Breakdown (Fully Diluted)</span>
            {/* Canadian ownership badge */}
            <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 10, background: canadianFD >= 0.5 ? greenLight : redLight, color: canadianFD >= 0.5 ? greenDark : red }}>
              CAN: {(canadianFD * 100).toFixed(1)}%
            </span>
          </div>
          {/* Stacked bar */}
          <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
            {categories.map(c => {
              const isActive = filterCategory === c.name;
              const isDimmed = filterCategory && !isActive;
              return (
                <div key={c.name}
                  title={`${c.name}: ${(c.pct * 100).toFixed(1)}% — click to ${c.name === 'Option Pool' || c.name === 'Purchased options' ? 'go to Stock Options' : 'filter'}`}
                  onClick={() => handleChartClick(c.name)}
                  style={{
                    width: `${c.pct * 100}%`, background: c.color, transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', opacity: isDimmed ? 0.3 : 1,
                    filter: isActive ? 'brightness(0.9)' : 'none',
                    boxShadow: isActive ? 'inset 0 0 0 2px rgba(0,0,0,0.15)' : 'none',
                  }}>
                  {c.pct >= 0.06 && <span style={{ fontSize: 9, color: '#555', fontWeight: 700, fontFamily: mono }}>{Math.round(c.pct * 100)}%</span>}
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {categories.map(c => {
              const isActive = filterCategory === c.name;
              return (
                <div key={c.name} onClick={() => handleChartClick(c.name)}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', opacity: filterCategory && !isActive ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: c.color }} />
                  <span style={{ fontFamily: sans, fontSize: 10, color: isActive ? t.textPrimary : t.textSecondary, fontWeight: isActive ? 600 : 400 }}>{c.name}</span>
                  <span style={{ fontFamily: mono, fontSize: 9, color: t.textMuted }}>{(c.pct * 100).toFixed(1)}%</span>
                </div>
              );
            })}
            {filterCategory && (
              <button onClick={() => setFilterCategory(null)} style={{
                fontFamily: sans, fontSize: 9, padding: '2px 8px', borderRadius: 10,
                border: `1px solid ${t.border}`, background: t.bg, color: t.textMuted, cursor: 'pointer',
              }}>Clear filter</button>
            )}
          </div>
        </div>
      )}

      {/* Scenario Modeling */}
      {showScenario && (() => {
        const postMoney = scenario.preMoney + scenario.roundSize;
        const pricePerShare = scenario.preMoney / data.fdTotal;
        const newShares = scenario.roundSize / pricePerShare;
        const newPoolShares = Math.round(data.fdTotal * (scenario.newPoolPct / 100)) - OPTION_POOL.total;
        const postFDTotal = data.fdTotal + newShares + Math.max(0, newPoolShares);
        const dilutionData = categories.map(c => ({
          ...c,
          currentPct: c.shares / data.fdTotal,
          postPct: c.shares / postFDTotal,
          dilution: c.shares / data.fdTotal - c.shares / postFDTotal,
        }));
        const newInvestorPct = newShares / postFDTotal;
        // Canadian ownership post-round (canPct = % of new round from CAN investors)
        const canSharesCurrent = data.rows.filter(r => r.country === 'CAN').reduce((s, r) => s + r.totalShares, 0);
        const canNewShares = Math.round(newShares * (scenario.canPct / 100));
        const canSharesPost = canSharesCurrent + canNewShares;
        const canPctPost = canSharesPost / postFDTotal;
        return (
          <div style={{ marginBottom: 16, padding: '16px', background: t.bgCard, border: `1px solid ${amber}`, borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: amber, fontFamily: sans }}>Scenario Modeling — Next Round</span>
              <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: `1px solid ${t.border}` }}>
                {['CAD', 'USD'].map(c => (
                  <button key={c} onClick={() => setScenario(prev => ({ ...prev, currency: c }))} style={{
                    fontFamily: mono, fontSize: 10, fontWeight: 600, padding: '3px 10px', border: 'none', cursor: 'pointer',
                    background: scenario.currency === c ? amber : t.bg, color: scenario.currency === c ? '#fff' : t.textSecondary,
                  }}>{c}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-end' }}>
              <div style={{ flex: '0 1 180px' }}>
                <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 3 }}>Round Size ({scenario.currency})</div>
                <input type="text" value={scenario.roundSize ? scenario.roundSize.toLocaleString('en-CA') : ''}
                  onChange={e => setScenario(prev => ({ ...prev, roundSize: parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0 }))}
                  style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: '0 1 180px' }}>
                <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 3 }}>Pre-Money ({scenario.currency})</div>
                <input type="text" value={scenario.preMoney ? scenario.preMoney.toLocaleString('en-CA') : ''}
                  onChange={e => setScenario(prev => ({ ...prev, preMoney: parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0 }))}
                  style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, boxSizing: 'border-box' }} />
              </div>
              <div style={{ flex: '0 0 60px' }}>
                <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 3 }}>Pool %</div>
                <input type="text" value={scenario.newPoolPct || ''}
                  onChange={e => setScenario(prev => ({ ...prev, newPoolPct: parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0 }))}
                  style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, boxSizing: 'border-box' }} />
              </div>
            </div>
            {/* Canadian investor split — compact */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, flexShrink: 0 }}>CAN/Non-CAN split:</span>
              <input type="range" min={0} max={100} step={5} value={scenario.canPct}
                onChange={e => setScenario(prev => ({ ...prev, canPct: parseInt(e.target.value) }))}
                style={{ flex: 1, accentColor: green }} />
              <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, color: green, flexShrink: 0, width: 70, textAlign: 'right' }}>{scenario.canPct}% CAN</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
              {[
                { label: 'Post-Money', value: fmtMoney(postMoney) },
                { label: 'Price/Share', value: `$${pricePerShare.toFixed(4)}` },
                { label: 'New Shares', value: fmt(Math.round(newShares)) },
                { label: 'New Investor %', value: `${(newInvestorPct * 100).toFixed(1)}%` },
              ].map(r => (
                <div key={r.label} style={{ padding: '8px 10px', background: amberLight, borderRadius: 6, textAlign: 'center' }}>
                  <div style={{ fontFamily: mono, fontSize: 14, fontWeight: 700, color: amber }}>{r.value}</div>
                  <div style={{ fontFamily: sans, fontSize: 8, color: t.textSecondary }}>{r.label}</div>
                </div>
              ))}
            </div>
            {/* Dilution + Canadian ownership */}
            <div style={{ display: 'grid', gridTemplateColumns: '140px 80px 80px 80px', gap: 4, padding: '4px 8px', fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>
              <span>Category</span><span style={{ textAlign: 'right' }}>Current %</span><span style={{ textAlign: 'right' }}>Post-Round %</span><span style={{ textAlign: 'right' }}>Dilution</span>
            </div>
            {dilutionData.map(d => (
              <div key={d.name} style={{ display: 'grid', gridTemplateColumns: '140px 80px 80px 80px', gap: 4, padding: '3px 8px', borderBottom: `1px solid ${t.borderLight}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                  <span style={{ fontFamily: sans, fontSize: 10, color: t.textPrimary }}>{d.name}</span>
                </div>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{(d.currentPct * 100).toFixed(1)}%</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{(d.postPct * 100).toFixed(1)}%</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: red, textAlign: 'right' }}>-{(d.dilution * 100).toFixed(1)}%</span>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: '140px 80px 80px 80px', gap: 4, padding: '3px 8px', borderBottom: `1px solid ${t.borderLight}` }}>
              <span style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, color: green }}>New Investor</span>
              <span style={{ fontFamily: mono, fontSize: 10, color: t.textMuted, textAlign: 'right' }}>--</span>
              <span style={{ fontFamily: mono, fontSize: 10, color: green, fontWeight: 600, textAlign: 'right' }}>{(newInvestorPct * 100).toFixed(1)}%</span>
              <span></span>
            </div>
            {/* Canadian ownership post-round */}
            <div style={{ marginTop: 8, padding: '6px 8px', background: canPctPost < 0.5 ? redLight : greenLight, borderRadius: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, color: canPctPost < 0.5 ? red : greenDark }}>Canadian Ownership (Post-Round)</span>
              <div>
                <span style={{ fontFamily: mono, fontSize: 10, color: t.textMuted }}>{(canadianFD * 100).toFixed(1)}%</span>
                <span style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, margin: '0 4px' }}>&rarr;</span>
                <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, color: canPctPost < 0.5 ? red : greenDark }}>{(canPctPost * 100).toFixed(1)}%</span>
              </div>
            </div>
            {canPctPost < 0.5 && (
              <div style={{ marginTop: 6, fontFamily: sans, fontSize: 9, color: red, fontWeight: 600, padding: '4px 8px', background: redLight, borderRadius: 4 }}>
                Canadian ownership below 50% — SPCC status and SR&ED eligibility may be at risk.
              </div>
            )}
          </div>
        );
      })()}

      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search shareholder..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ fontFamily: sans, fontSize: 12, padding: '5px 10px', border: `1px solid ${t.border}`, borderRadius: 4, width: 200, background: t.bg }} />
        <button onClick={() => setShowChart(!showChart)} style={{ fontFamily: sans, fontSize: 10, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary }}>{showChart ? 'Hide Chart' : 'Show Chart'}</button>
        <button onClick={() => setShowScenario(!showScenario)} style={{ fontFamily: sans, fontSize: 10, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${showScenario ? amber : t.border}`, background: showScenario ? amberLight : t.bg, color: showScenario ? amber : t.textSecondary }}>{showScenario ? 'Hide Scenario' : 'Scenario Modeling'}</button>
        <button onClick={() => setShowRaiseWizard(true)} style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, padding: '4px 12px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${coral}`, background: coral, color: '#fff' }}>+ Raise a Round</button>
        {auditLog.length > 0 && (
          <button onClick={() => setShowAudit(!showAudit)} style={{ fontFamily: sans, fontSize: 10, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary }}>
            Audit Log ({auditLog.length})
          </button>
        )}
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={exportCSV} style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 4, border: `1px solid ${coral}`, background: coralSoft, color: coral, cursor: 'pointer' }}>Export CSV</button>
        </div>
      </div>

      {/* Audit Log Drawer */}
      {showAudit && (
        <div style={{ marginBottom: 12, padding: '12px', background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: 8, maxHeight: 200, overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontFamily: sans, fontSize: 11, fontWeight: 600, color: t.textPrimary }}>Audit Log ({auditLog.length} entries)</span>
            <button onClick={exportAuditCSV} style={{ fontFamily: sans, fontSize: 9, padding: '2px 8px', borderRadius: 4, border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary, cursor: 'pointer' }}>Export CSV</button>
          </div>
          {auditLog.slice().reverse().map(e => (
            <div key={e.id} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: `1px solid ${t.borderLight}`, fontSize: 9, fontFamily: mono }}>
              <span style={{ color: t.textMuted, width: 130, flexShrink: 0 }}>{new Date(e.timestamp).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              <span style={{ color: t.textSecondary, width: 30 }}>{e.user}</span>
              <span style={{ color: amber, width: 50 }}>{e.action}</span>
              <span style={{ color: t.textPrimary, flex: 1 }}>{e.field}{e.shareholder ? ` (${e.shareholder})` : ''}: {e.oldValue || '--'} &rarr; {e.newValue || '--'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `minmax(180px,2fr) 40px repeat(${rounds.length}, minmax(60px,1fr)) minmax(80px,1fr) minmax(65px,1fr) minmax(65px,1fr)`,
          gap: 2, padding: '6px 6px', background: green, borderRadius: '6px 6px 0 0',
          fontFamily: sans, fontSize: 8, fontWeight: 600, color: '#fff',
        }}>
          <span onClick={() => handleCapSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
            Shareholder {capSortBy === 'name' ? (capSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
          </span>
          <span onClick={() => handleCapSort('country')} style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}>
            Ctry {capSortBy === 'country' ? (capSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
          </span>
          {rounds.map(r => (
            <span key={r.id}
              onClick={() => handleCapSort(r.id)}
              onDoubleClick={r.price ? (e) => { e.stopPropagation(); startPriceEdit(r.id, r.price); } : undefined}
              style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              title={r.price ? 'Click to sort, double-click to edit price' : 'Click to sort'}>
              {r.short} {capSortBy === r.id ? (capSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
              {editingPrice === r.id ? (
                <input type="number" step="0.0001" value={priceValue} autoFocus
                  onChange={e => setPriceValue(e.target.value)}
                  onBlur={commitPriceEdit}
                  onKeyDown={e => { if (e.key === 'Enter') commitPriceEdit(); if (e.key === 'Escape') setEditingPrice(null); }}
                  onClick={e => e.stopPropagation()}
                  style={{ fontFamily: mono, fontSize: 8, width: 55, textAlign: 'right', padding: '1px 2px', border: '1px solid #fff', borderRadius: 2, background: 'rgba(255,255,255,0.9)', color: '#333' }}
                />
              ) : (
                r.price && <div style={{ opacity: 0.8, fontSize: 7 }}>${r.price.toFixed(r.price < 1 ? 3 : 4)}</div>
              )}
            </span>
          ))}
          <span onClick={() => handleCapSort('totalShares')} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>
            # Shares {capSortBy === 'totalShares' ? (capSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
          </span>
          <span onClick={() => handleCapSort('undilutedPct')} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>
            Undiluted {capSortBy === 'undilutedPct' ? (capSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
          </span>
          <span onClick={() => handleCapSort('fullyDilutedPct')} style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}>
            Fully Dil. {capSortBy === 'fullyDilutedPct' ? (capSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
          </span>
        </div>

        {filtered.map((row, i) => {
          const rowIdx = shareholders.findIndex(s => s.name === row.name);
          return (
            <div key={i} style={{
              display: 'grid',
              gridTemplateColumns: `minmax(180px,2fr) 40px repeat(${rounds.length}, minmax(60px,1fr)) minmax(80px,1fr) minmax(65px,1fr) minmax(65px,1fr)`,
              gap: 2, padding: '4px 6px',
              background: i % 2 === 0 ? t.bg : t.bgSurface,
              borderBottom: `1px solid ${t.borderLight}`, fontSize: 10,
            }}>
              <span onClick={() => { setSelectedShareholder(selectedShareholder === row.name ? null : row.name); setEditingName(false); setEditingPanelRound(null); }}
                style={{ fontFamily: sans, color: t.textPrimary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                title={`${row.name} (${getCat(row)})`}>
                {row.name}
              </span>
              <span style={{ fontFamily: mono, fontSize: 8, color: t.textMuted, textAlign: 'center' }}>{row.country}</span>
              {rounds.map(r => {
                const val = row.h[r.id];
                const isEditing = editingCell?.rowIdx === rowIdx && editingCell?.roundId === r.id;
                return isEditing ? (
                  <input key={r.id} type="number" value={editValue} autoFocus
                    onChange={e => setEditValue(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingCell(null); }}
                    style={{ fontFamily: mono, fontSize: 10, textAlign: 'right', width: '100%', padding: '0 2px', border: `1px solid ${green}`, borderRadius: 2, background: greenLight }}
                  />
                ) : (
                  <span key={r.id}
                    style={{ fontFamily: mono, color: t.textPrimary, textAlign: 'right' }}>
                    {fmt(val)}
                  </span>
                );
              })}
              <span style={{ fontFamily: mono, color: t.textPrimary, textAlign: 'right', fontWeight: 600 }}>{fmt(row.totalShares)}</span>
              <span style={{ fontFamily: mono, color: t.textSecondary, textAlign: 'right' }}>{pctFmt(row.undilutedPct)}</span>
              <span style={{ fontFamily: mono, color: green, textAlign: 'right', fontWeight: 600 }}>{pctFmt(row.fullyDilutedPct)}</span>
            </div>
          );
        })}

        {/* Total */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `minmax(180px,2fr) 40px repeat(${rounds.length}, minmax(60px,1fr)) minmax(80px,1fr) minmax(65px,1fr) minmax(65px,1fr)`,
          gap: 2, padding: '6px 6px', background: green, borderRadius: '0 0 6px 6px',
          fontFamily: mono, fontSize: 10, fontWeight: 700, color: '#fff',
        }}>
          <span style={{ fontFamily: sans }}>TOTAL ({filtered.length})</span>
          <span></span>
          {rounds.map(r => {
            const total = filtered.reduce((s, row) => s + (row.h[r.id] || 0), 0);
            return <span key={r.id} style={{ textAlign: 'right' }}>{total > 0 ? fmt(total) : ''}</span>;
          })}
          <span style={{ textAlign: 'right' }}>{fmt(data.totalShares)}</span>
          <span style={{ textAlign: 'right' }}>100%</span>
          <span style={{ textAlign: 'right' }}>100%</span>
        </div>

        {/* Option pool */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `minmax(180px,2fr) 40px repeat(${rounds.length}, minmax(60px,1fr)) minmax(80px,1fr) minmax(65px,1fr) minmax(65px,1fr)`,
          gap: 2, padding: '4px 6px', marginTop: 2, background: greenLight, borderRadius: 4,
          fontFamily: mono, fontSize: 10, color: greenDark,
        }}>
          <span style={{ fontFamily: sans, fontWeight: 600 }}>Stock Option Pool</span>
          <span></span>
          {rounds.map(r => <span key={r.id}></span>)}
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{fmt(Math.round(OPTION_POOL.outstanding))}</span>
          <span style={{ textAlign: 'right' }}>--</span>
          <span style={{ textAlign: 'right', fontWeight: 600 }}>{pctFmt(OPTION_POOL.outstanding / data.fdTotal)}</span>
        </div>
      </div>

      <div style={{ marginTop: 8, fontFamily: sans, fontSize: 9, color: t.textMuted }}>
        Double-click any share count or round price to edit. Click a shareholder name to view details.
      </div>

      {/* Shareholder detail drawer */}
      {selectedShareholder && (() => {
        const sh = data.rows.find(r => r.name === selectedShareholder);
        if (!sh) return null;
        const shAudit = auditLog.filter(e => e.shareholder === selectedShareholder);
        return (
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
            background: t.bgCard || '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
            zIndex: 200, display: 'flex', flexDirection: 'column', fontFamily: sans,
          }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, marginRight: 8 }}>
                {editingName ? (<>
                  <input type="text" defaultValue={sh.name} autoFocus
                    onBlur={e => {
                      const newName = e.target.value.trim();
                      if (newName && newName !== selectedShareholder) {
                        const oldName = selectedShareholder;
                        setShareholders(prev => prev.map(s => s.name === oldName ? { ...s, name: newName } : s));
                        logChange('update', 'name', oldName, newName, newName);
                        setSelectedShareholder(newName);
                      }
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingName(false); }}
                    style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: sans, border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, width: '100%', padding: '2px 6px', outline: 'none', marginBottom: 4 }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    <span style={{ fontSize: 10, color: t.textSecondary }}>{getCat(sh)} |</span>
                    <select defaultValue={sh.country}
                      onChange={e => {
                        const newCountry = e.target.value;
                        setShareholders(prev => prev.map(s => s.name === sh.name ? { ...s, country: newCountry } : s));
                        logChange('update', 'country', sh.country, newCountry, sh.name);
                      }}
                      style={{ fontSize: 10, color: t.textSecondary, fontFamily: sans, border: `1px solid ${t.border}`, borderRadius: 3, background: t.bg, padding: '1px 4px', outline: 'none' }}>
                      <option value="CAN">CAN</option>
                      <option value="INT">INT</option>
                    </select>
                    <button onClick={() => setEditingName(false)} style={{ fontSize: 9, fontFamily: sans, padding: '2px 8px', borderRadius: 3, border: `1px solid ${green}`, background: green, color: '#fff', cursor: 'pointer', marginLeft: 'auto' }}>Done</button>
                  </div>
                </>) : (<>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary }}>{sh.name}</span>
                    <span onClick={() => setEditingName(true)} style={{ cursor: 'pointer', fontSize: 12, color: t.textMuted, opacity: 0.5 }} title="Edit">&#9998;</span>
                  </div>
                  <div style={{ fontSize: 10, color: t.textSecondary, marginTop: 2 }}>{getCat(sh)} | {sh.country}</div>
                </>)}
              </div>
              <button onClick={() => setSelectedShareholder(null)} style={{ border: 'none', background: t.inputBg, borderRadius: 6, width: 28, height: 28, cursor: 'pointer', fontSize: 14, color: t.textSecondary, flexShrink: 0 }}>&times;</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
              <div style={{ padding: '10px', background: greenLight, borderRadius: 6, marginBottom: 12 }}>
                <div style={{ fontFamily: mono, fontSize: 18, fontWeight: 700, color: green }}>{fmt(sh.totalShares)}</div>
                <div style={{ fontFamily: sans, fontSize: 9, color: greenDark }}>Total shares | {pctFmt(sh.fullyDilutedPct)} FD</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Investment by Round</div>
              {rounds.filter(r => sh.h[r.id]).map(r => {
                const invested = r.price ? sh.h[r.id] * r.price : 0;
                const isEditingThis = editingPanelRound === r.id;
                return (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: `1px solid ${t.borderLight}` }}>
                  <span style={{ fontSize: 10, color: t.textSecondary }}>{r.name}{r.price ? ` @ $${r.price.toFixed(4)}` : ''}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isEditingThis ? (
                      <input type="text" value={panelEditValue} autoFocus
                        onChange={e => setPanelEditValue(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={() => {
                          const newVal = parseInt(panelEditValue) || 0;
                          const oldVal = sh.h[r.id];
                          if (newVal > 0 && newVal !== Math.round(oldVal)) {
                            const shIdx = shareholders.findIndex(s => s.name === sh.name);
                            if (shIdx >= 0) {
                              logChange('update', `${r.id} shares`, Math.round(oldVal), newVal, sh.name);
                              setShareholders(prev => prev.map((s, i) => i === shIdx ? { ...s, h: { ...s.h, [r.id]: newVal } } : s));
                            }
                          }
                          setEditingPanelRound(null);
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setEditingPanelRound(null); }}
                        style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, width: 80, padding: '2px 4px', border: `1px solid ${t.border}`, borderRadius: 3, background: t.bg, textAlign: 'right', outline: 'none' }} />
                    ) : (
                      <div style={{ textAlign: 'right' }}>
                        <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, fontWeight: 600 }}>{fmt(sh.h[r.id])} shares</span>
                        {invested > 0 && <div style={{ fontFamily: mono, fontSize: 10, color: green, fontWeight: 600 }}>${invested.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>}
                      </div>
                    )}
                    {!isEditingThis && <span onClick={() => { setEditingPanelRound(r.id); setPanelEditValue(String(Math.round(sh.h[r.id]))); }} style={{ cursor: 'pointer', fontSize: 10, color: t.textMuted, opacity: 0.4 }} title="Edit shares">&#9998;</span>}
                  </div>
                </div>
                );
              })}
              {(() => {
                const totalInvested = rounds.reduce((sum, r) => sum + (sh.h[r.id] && r.price ? sh.h[r.id] * r.price : 0), 0);
                return totalInvested > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', marginTop: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: t.textPrimary }}>Total Invested</span>
                    <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: green }}>${totalInvested.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  </div>
                );
              })()}
              {shAudit.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Change History</div>
                  {shAudit.map(e => (
                    <div key={e.id} style={{ fontSize: 9, fontFamily: mono, padding: '3px 0', borderBottom: `1px solid ${t.borderLight}`, color: t.textSecondary }}>
                      {new Date(e.timestamp).toLocaleDateString('en-CA')} — {e.field}: {e.oldValue} &rarr; {e.newValue}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}
      {selectedShareholder && <div onClick={() => setSelectedShareholder(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 199 }} />}

      {/* Raise a Round Wizard — 7 steps */}
      {showRaiseWizard && (() => {
        const STEP_LABELS = ['Basics', 'Valuation', 'ESOP', 'Investors', 'Terms', 'Pro-Forma', 'Review'];
        const totalSteps = raiseData.esopShuffle ? 7 : 6; // skip step 3 if no ESOP shuffle
        const inputStyle = { fontFamily: mono, fontSize: 12, width: '100%', padding: '6px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg };
        const labelStyle = { fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 3 };
        const rc = raiseCalc;
        return (
        <>
          <div onClick={() => { setShowRaiseWizard(false); setRaiseStep(1); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 299 }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: t.bgCard || '#fff', borderRadius: 12, padding: 24, width: 560, maxHeight: '85vh', overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', zIndex: 300,
          }}>
            <div style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 4 }}>Raise a Round</div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
              {STEP_LABELS.map((label, i) => {
                const step = i + 1;
                const skip = !raiseData.esopShuffle && step === 3;
                if (skip) return null;
                return (
                  <div key={step} onClick={() => setRaiseStep(step)} style={{
                    flex: 1, textAlign: 'center', padding: '4px 0', borderRadius: 4, cursor: 'pointer',
                    background: raiseStep === step ? green : step < raiseStep ? greenLight : t.borderLight,
                    fontFamily: sans, fontSize: 8, fontWeight: 600,
                    color: raiseStep === step ? '#fff' : step < raiseStep ? greenDark : t.textMuted,
                  }}>{label}</div>
                );
              })}
            </div>

            {/* Step 1 — Round Basics */}
            {raiseStep === 1 && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div><div style={labelStyle}>Round Name</div><input type="text" value={raiseData.name} onChange={e => setRaiseData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Series B" style={inputStyle} /></div>
                <div><div style={labelStyle}>Round Type</div>
                  <select value={raiseData.roundType} onChange={e => setRaiseData(p => ({ ...p, roundType: e.target.value }))} style={inputStyle}>
                    {['Equity', 'Convertible Note', 'SAFE'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 8 }}>
                  <div><div style={labelStyle}>Target Raise Amount</div><input type="number" value={raiseData.roundSize || ''} onChange={e => setRaiseData(p => ({ ...p, roundSize: parseFloat(e.target.value) || 0 }))} style={inputStyle} /></div>
                  <div><div style={labelStyle}>Currency</div>
                    <select value={raiseData.currency} onChange={e => setRaiseData(p => ({ ...p, currency: e.target.value }))} style={inputStyle}>
                      {['CAD', 'USD'].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div><div style={labelStyle}>Expected Close Date</div><input type="date" value={raiseData.closeDate} onChange={e => setRaiseData(p => ({ ...p, closeDate: e.target.value }))} style={inputStyle} /></div>
              </div>
            )}

            {/* Step 2 — Valuation & Pricing */}
            {raiseStep === 2 && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div><div style={labelStyle}>Pre-Money Valuation ({raiseData.currency})</div><input type="number" value={raiseData.preMoney || ''} onChange={e => setRaiseData(p => ({ ...p, preMoney: parseFloat(e.target.value) || 0 }))} style={inputStyle} /></div>
                {rc.pps > 0 && (
                  <div style={{ padding: '10px 12px', background: greenLight, borderRadius: 6 }}>
                    <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: green }}>${rc.pps.toFixed(4)} / share</div>
                    <div style={{ fontFamily: sans, fontSize: 9, color: greenDark, marginTop: 2 }}>Based on {fmt(Math.round(data.fdTotal))} fully diluted shares</div>
                  </div>
                )}
                <div style={{ fontSize: 10, color: t.textSecondary, fontFamily: sans }}>
                  Current: {fmt(Math.round(data.totalShares))} shares + {fmt(Math.round(OPTION_POOL.outstanding))} options outstanding + {fmt(Math.round(OPTION_POOL.available))} available
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={raiseData.esopShuffle} onChange={e => setRaiseData(p => ({ ...p, esopShuffle: e.target.checked }))} />
                  <span style={{ fontFamily: sans, fontSize: 11, color: t.textPrimary }}>Include option pool expansion in pre-money (ESOP shuffle)</span>
                </label>
              </div>
            )}

            {/* Step 3 — ESOP (conditional) */}
            {raiseStep === 3 && raiseData.esopShuffle && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ fontSize: 10, color: t.textSecondary, fontFamily: sans }}>
                  Current pool: {fmt(OPTION_POOL.total)} ({fmt(Math.round(OPTION_POOL.available))} available, {Math.round((OPTION_POOL.outstanding / OPTION_POOL.total) * 100)}% utilized)
                </div>
                <div><div style={labelStyle}>Target Post-Round Pool %</div><input type="number" value={raiseData.targetPoolPct || ''} onChange={e => setRaiseData(p => ({ ...p, targetPoolPct: parseFloat(e.target.value) || 0 }))} style={inputStyle} /></div>
                {rc.poolExpansion > 0 && (
                  <div style={{ padding: '8px 10px', background: amberLight, borderRadius: 6, fontFamily: mono, fontSize: 11, color: amber }}>
                    +{fmt(rc.poolExpansion)} new shares to authorize (dilution before new investor)
                  </div>
                )}
              </div>
            )}

            {/* Step 4 — Investors */}
            {raiseStep === 4 && (
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 60px 30px', gap: 6, fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted }}>
                  <span>Investor Name</span><span style={{ textAlign: 'right' }}>Amount ({raiseData.currency})</span><span style={{ textAlign: 'center' }}>CAN?</span><span></span>
                </div>
                {raiseData.investors.map((inv, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 60px 30px', gap: 6, alignItems: 'center' }}>
                    <input type="text" value={inv.name} placeholder="Investor name" onChange={e => setRaiseData(p => { const inv2 = [...p.investors]; inv2[i] = { ...inv2[i], name: e.target.value }; return { ...p, investors: inv2 }; })} style={{ ...inputStyle, fontSize: 11 }} />
                    <input type="number" value={inv.amount || ''} onChange={e => setRaiseData(p => { const inv2 = [...p.investors]; inv2[i] = { ...inv2[i], amount: parseFloat(e.target.value) || 0 }; return { ...p, investors: inv2 }; })} style={{ ...inputStyle, fontSize: 11, textAlign: 'right' }} />
                    <div style={{ textAlign: 'center' }}>
                      <input type="checkbox" checked={inv.canadian} onChange={e => setRaiseData(p => { const inv2 = [...p.investors]; inv2[i] = { ...inv2[i], canadian: e.target.checked }; return { ...p, investors: inv2 }; })} />
                    </div>
                    {raiseData.investors.length > 1 && (
                      <button onClick={() => setRaiseData(p => ({ ...p, investors: p.investors.filter((_, j) => j !== i) }))} style={{ border: 'none', background: 'none', color: red, cursor: 'pointer', fontSize: 14 }}>&times;</button>
                    )}
                  </div>
                ))}
                <button onClick={() => setRaiseData(p => ({ ...p, investors: [...p.investors, { name: '', amount: 0, canadian: false }] }))}
                  style={{ fontFamily: sans, fontSize: 10, padding: '4px 10px', border: `1px dashed ${t.border}`, borderRadius: 4, background: 'transparent', color: t.textSecondary, cursor: 'pointer' }}>+ Add investor</button>
                {/* Progress bar */}
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, fontFamily: mono, color: t.textMuted, marginBottom: 4 }}>
                    <span>Committed: {fmtMoney(rc.totalInvested)}</span>
                    <span>Target: {fmtMoney(raiseData.roundSize)}</span>
                  </div>
                  <div style={{ height: 6, background: t.borderLight, borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min((rc.totalInvested / (raiseData.roundSize || 1)) * 100, 100)}%`, background: rc.totalInvested >= raiseData.roundSize ? green : amber, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5 — Key Terms */}
            {raiseStep === 5 && (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div><div style={labelStyle}>Liquidation Preference</div>
                    <select value={raiseData.liquidationPref} onChange={e => setRaiseData(p => ({ ...p, liquidationPref: e.target.value }))} style={inputStyle}>
                      {['1x non-participating', '1x participating', '2x non-participating', '2x participating'].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div><div style={labelStyle}>Anti-Dilution</div>
                    <select value={raiseData.antiDilution} onChange={e => setRaiseData(p => ({ ...p, antiDilution: e.target.value }))} style={inputStyle}>
                      {['Broad-based weighted avg', 'Full ratchet', 'None'].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10 }}>
                  <div><div style={labelStyle}>Board Seats</div><input type="number" value={raiseData.boardSeats || ''} onChange={e => setRaiseData(p => ({ ...p, boardSeats: parseInt(e.target.value) || 0 }))} style={inputStyle} /></div>
                  <div><div style={labelStyle}>Seat Holder</div><input type="text" value={raiseData.boardSeatHolder || ''} onChange={e => setRaiseData(p => ({ ...p, boardSeatHolder: e.target.value }))} style={inputStyle} /></div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={raiseData.proRata} onChange={e => setRaiseData(p => ({ ...p, proRata: e.target.checked }))} />
                  <span style={{ fontFamily: sans, fontSize: 11, color: t.textPrimary }}>Pro-rata rights</span>
                </label>
                <div><div style={labelStyle}>Side Letters / Special Provisions</div>
                  <textarea value={raiseData.sideLetters || ''} onChange={e => setRaiseData(p => ({ ...p, sideLetters: e.target.value }))} rows={3}
                    style={{ ...inputStyle, fontFamily: sans, resize: 'vertical' }} placeholder="Any additional terms or notes..." />
                </div>
              </div>
            )}

            {/* Step 6 — Pro-Forma Cap Table */}
            {raiseStep === 6 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Pro-Forma Cap Table Impact</div>
                <div style={{ display: 'grid', gridTemplateColumns: '140px 80px 80px 80px', gap: 4, padding: '4px 8px', fontFamily: sans, fontSize: 9, fontWeight: 600, color: t.textMuted, borderBottom: `1px solid ${t.border}` }}>
                  <span>Category</span><span style={{ textAlign: 'right' }}>Current %</span><span style={{ textAlign: 'right' }}>Post-Round %</span><span style={{ textAlign: 'right' }}>Dilution</span>
                </div>
                {categories.map(c => {
                  const cur = c.shares / data.fdTotal;
                  const post = c.shares / (rc.postFD || data.fdTotal);
                  return (
                    <div key={c.name} style={{ display: 'grid', gridTemplateColumns: '140px 80px 80px 80px', gap: 4, padding: '3px 8px', borderBottom: `1px solid ${t.borderLight}` }}>
                      <span style={{ fontFamily: sans, fontSize: 10, color: t.textPrimary }}>{c.name}</span>
                      <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{(cur * 100).toFixed(1)}%</span>
                      <span style={{ fontFamily: mono, fontSize: 10, color: t.textPrimary, textAlign: 'right' }}>{(post * 100).toFixed(1)}%</span>
                      <span style={{ fontFamily: mono, fontSize: 10, color: red, textAlign: 'right' }}>-{((cur - post) * 100).toFixed(1)}%</span>
                    </div>
                  );
                })}
                <div style={{ display: 'grid', gridTemplateColumns: '140px 80px 80px 80px', gap: 4, padding: '3px 8px', borderBottom: `1px solid ${t.borderLight}` }}>
                  <span style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, color: green }}>New Investors</span>
                  <span style={{ fontFamily: mono, fontSize: 10, color: t.textMuted, textAlign: 'right' }}>--</span>
                  <span style={{ fontFamily: mono, fontSize: 10, color: green, fontWeight: 600, textAlign: 'right' }}>{rc.postFD ? ((rc.newShares / rc.postFD) * 100).toFixed(1) : 0}%</span>
                  <span></span>
                </div>
                {/* Canadian ownership */}
                <div style={{ marginTop: 8, padding: '6px 8px', background: (rc.canPctPost || 0) < 0.5 ? redLight : greenLight, borderRadius: 4, display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontFamily: sans, fontSize: 10, fontWeight: 600, color: (rc.canPctPost || 0) < 0.5 ? red : greenDark }}>Canadian Ownership</span>
                  <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 600, color: (rc.canPctPost || 0) < 0.5 ? red : greenDark }}>{((rc.canPctPost || 0) * 100).toFixed(1)}%</span>
                </div>
                {(rc.canPctPost || 0) < 0.5 && (
                  <div style={{ marginTop: 4, fontFamily: sans, fontSize: 9, color: red, fontWeight: 600, padding: '4px 8px', background: redLight, borderRadius: 4 }}>
                    Canadian ownership below 50% — SPCC status and SR&ED eligibility at risk
                  </div>
                )}
              </div>
            )}

            {/* Step 7 — Review & Confirm */}
            {raiseStep === 7 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: t.textPrimary, marginBottom: 8 }}>Review & Confirm</div>
                {[
                  ['Round', raiseData.name],
                  ['Type', raiseData.roundType],
                  ['Currency', raiseData.currency],
                  ['Total Committed', fmtMoney(rc.totalInvested)],
                  ['Pre-Money', fmtMoney(raiseData.preMoney)],
                  ['Post-Money', fmtMoney(rc.postMoney)],
                  ['Price/Share', `$${(rc.pps || 0).toFixed(4)}`],
                  ['New Shares', fmt(rc.newShares)],
                  ['Investors', raiseData.investors.filter(i => i.name).map(i => i.name).join(', ') || '--'],
                  ['Liq. Preference', raiseData.liquidationPref],
                  ['Anti-Dilution', raiseData.antiDilution],
                  ['Board Seats', raiseData.boardSeats > 0 ? `${raiseData.boardSeats} (${raiseData.boardSeatHolder})` : 'None'],
                  ['Pro-Rata', raiseData.proRata ? 'Yes' : 'No'],
                  ['CAN Ownership Post', `${((rc.canPctPost || 0) * 100).toFixed(1)}%`],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${t.borderLight}`, fontFamily: mono, fontSize: 10 }}>
                    <span style={{ color: t.textSecondary }}>{k}</span>
                    <span style={{ color: t.textPrimary, fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
                {raiseData.sideLetters && (
                  <div style={{ marginTop: 8, fontFamily: sans, fontSize: 10, color: t.textSecondary }}>
                    <span style={{ fontWeight: 600 }}>Notes:</span> {raiseData.sideLetters}
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <button onClick={() => {
                if (raiseStep === 1) { setShowRaiseWizard(false); }
                else if (raiseStep === 4 && !raiseData.esopShuffle) setRaiseStep(2);
                else setRaiseStep(s => s - 1);
              }} style={{ fontFamily: sans, fontSize: 12, padding: '6px 14px', borderRadius: 6, border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary, cursor: 'pointer' }}>
                {raiseStep === 1 ? 'Cancel' : 'Back'}
              </button>
              <div style={{ display: 'flex', gap: 8 }}>
                {raiseStep === 7 && (
                  <button onClick={() => { raiseData.status = 'draft'; setShowRaiseWizard(false); }}
                    style={{ fontFamily: sans, fontSize: 12, padding: '6px 14px', borderRadius: 6, border: `1px solid ${amber}`, background: amberLight, color: amber, cursor: 'pointer' }}>
                    Save as Draft
                  </button>
                )}
                {raiseStep < 7 ? (
                  <button onClick={() => {
                    if (raiseStep === 2 && !raiseData.esopShuffle) setRaiseStep(4);
                    else setRaiseStep(s => s + 1);
                  }} disabled={raiseStep === 1 && !raiseData.name}
                    style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, border: `1px solid ${green}`, background: green, color: '#fff', cursor: 'pointer' }}>
                    Next
                  </button>
                ) : (
                  <button onClick={executeRound}
                    style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6, border: `1px solid ${green}`, background: green, color: '#fff', cursor: 'pointer' }}>
                    Finalize Round
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
        );
      })()}
    </div>
  );
}
